import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Passenger } from '@aviata/booking-data';
import type { FreeAlert } from 'freedom-shared';

/**
 * Owns the two transient alerts that appear after a saved-passenger edit
 * lands (account mutation success / failure). Bundles the template refs
 * with handlers that close the opposite alert first so they never
 * overlap. The component still owns the FreeAlert markup and binds these
 * refs via `ref="editSuccessAlertRef"` / `ref="editErrorAlertRef"`.
 */
export const useEditResultAlerts = () => {
  const { t } = useI18n();
  const editSuccessAlertRef = ref<InstanceType<typeof FreeAlert>>();
  const editErrorAlertRef = ref<InstanceType<typeof FreeAlert>>();

  const handleEditSuccess = (passenger: Passenger) => {
    const name = `${passenger.firstName} ${passenger.lastName}`.trim();
    editErrorAlertRef.value?.hide();
    editSuccessAlertRef.value?.show(
      t('passengersData.editAlert.success', { name }),
    );
  };

  const handleEditError = () => {
    editSuccessAlertRef.value?.hide();
    editErrorAlertRef.value?.show();
  };

  return {
    editSuccessAlertRef,
    editErrorAlertRef,
    handleEditSuccess,
    handleEditError,
  };
};
